import type React from 'react';
import { memo, useCallback, useState } from 'react';
import { Badge } from '@savvy-kit/components/badge';
import { Button } from '@savvy-kit/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { Filter, X } from 'lucide-react';

import type { Column, DataTableFilterActions, FiltersState, FilterStrategy } from '../core/types';
import type { Locale } from '../lib/i18n';
import { t } from '../lib/i18n';
import { FilterCategoryPanel } from './filter-category-panel';
import { FilterOptionsPanel } from './filter-options-panel';

export type ExtraItem = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
};

interface FilterSelectorProps<TData> {
  filters: FiltersState;
  columns: Column<TData>[];
  actions: DataTableFilterActions;
  strategy: FilterStrategy;
  locale?: Locale;
  searchableColumnIds?: string[];
  footer?: React.ReactNode | ((close: () => void) => React.ReactNode);
  extraItems?: ExtraItem[];
  resultCountLabel?: string;
  triggerSize?: 'default' | 'sm';
}

export const FilterSelector = memo(__FilterSelector) as typeof __FilterSelector;

function __FilterSelector<TData>({
  filters,
  columns,
  actions,
  strategy: _strategy,
  locale = 'en',
  searchableColumnIds = [],
  footer,
  extraItems: extraItemsProp,
  resultCountLabel,
  triggerSize = 'default',
}: FilterSelectorProps<TData>) {
  const [open, setOpen] = useState(false);
  const [activeColumnId, setActiveColumnId] = useState<string>('');
  const [activePanelOffsetTop, setActivePanelOffsetTop] = useState(0);

  const filterableColumns = columns.filter(
    (c): c is Column<TData, 'option' | 'multiOption' | 'number'> =>
      c.type === 'option' || c.type === 'multiOption' || c.type === 'number',
  );

  const totalFilterCount = filters.reduce((acc, f) => acc + f.values.length + (f.excludedValues?.length ?? 0), 0);
  const hasFilters = totalFilterCount > 0;

  const handleOpenChange = useCallback((value: boolean) => {
    setOpen(value);
    if (!value) {
      setActiveColumnId('');
      setActivePanelOffsetTop(0);
    }
  }, []);

  const handleSelectColumn = useCallback((columnId: string) => {
    setActiveColumnId(columnId);
  }, []);

  const handleClear = useCallback(() => {
    actions.removeAllFilters();
    setOpen(false);
  }, [actions]);

  const activeColumn = filterableColumns.find((col) => col.id === activeColumnId);
  // A grouped column renders its own cascade of cards (group list + the drilled
  // group's options), so the selector must not wrap it in the single options
  // card it uses for flat columns. Uses `every` to match the panel's own
  // all-or-nothing guard — a mixed column falls back to the flat card here too.
  const activeColumnGroupedOptions =
    !isNil(activeColumn) && activeColumn.type !== 'number'
      ? (activeColumn as Column<TData, 'option' | 'multiOption'>).getOptions()
      : [];
  const activeColumnIsGrouped =
    activeColumnGroupedOptions.length > 0 && activeColumnGroupedOptions.every((o) => !isNil(o.group));
  const isActiveColumnSearchable = searchableColumnIds.includes(activeColumnId);
  const searchHeaderCompensationPx = isActiveColumnSearchable ? 41 : 0;
  const alignedPanelOffsetTop = Math.max(activePanelOffsetTop - searchHeaderCompensationPx, 0);
  const resolvedFooter = typeof footer === 'function' ? footer(() => setOpen(false)) : footer;

  const extraItems = extraItemsProp ?? [];

  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          render={
            <Button
              variant="secondary"
              size={triggerSize}
              className={cn('gap-1.5 border-gray-500', hasFilters && 'rounded-r-none border-r-0')}
            />
          }
        >
          <Filter className="size-4 text-gray-500" />
          <span>{t('filter', locale)}</span>
          {hasFilters && (
            <Badge variant="primary" type="circle" className="h-5 min-w-5 text-xs px-1">
              {totalFilterCount}
            </Badge>
          )}
        </PopoverTrigger>
        <PopoverContent
          data-testid="data-table-filter-popover"
          align="start"
          side="bottom"
          className="w-fit bg-transparent border-none shadow-none p-0 flex gap-2 origin-(--transform-origin)"
        >
          <div className="self-start bg-white rounded-md border border-light shadow-md overflow-hidden">
            <FilterCategoryPanel
              columns={filterableColumns}
              filters={filters}
              activeColumnId={activeColumnId}
              onSelect={handleSelectColumn}
              onActiveOffsetChange={setActivePanelOffsetTop}
              footer={resolvedFooter}
              extraItems={extraItems}
            />
          </div>
          {!isNil(activeColumn) &&
            (activeColumnIsGrouped ? (
              // Grouped columns own their own cascade of cards; only carry the
              // vertical offset so the cascade lines up with the clicked
              // dimension when it isn't the first in the list.
              <div className="self-start" style={{ transform: `translateY(${alignedPanelOffsetTop}px)` }}>
                <FilterOptionsPanel
                  key={activeColumnId}
                  column={activeColumn}
                  filters={filters}
                  actions={actions}
                  searchable={isActiveColumnSearchable}
                />
              </div>
            ) : (
              <div
                className="bg-white rounded-md border border-light shadow-md overflow-y-auto self-start max-h-60"
                style={{ transform: `translateY(${alignedPanelOffsetTop}px)` }}
              >
                <FilterOptionsPanel
                  key={activeColumnId}
                  column={activeColumn}
                  filters={filters}
                  actions={actions}
                  searchable={isActiveColumnSearchable}
                />
              </div>
            ))}
        </PopoverContent>
      </Popover>
      {hasFilters && (
        <button
          type="button"
          data-testid="clear-filters"
          aria-label="Clear filters"
          className={cn(
            'px-2 border border-gray-500 rounded-r-md hover:bg-component-hover transition-colors cursor-pointer',
            triggerSize === 'sm' ? 'h-8' : 'h-10',
          )}
          onClick={handleClear}
        >
          <X className="size-3" />
        </button>
      )}
      <span
        aria-live="polite"
        className={cn('text-sm text-[var(--muted-foreground)] whitespace-nowrap', !isNil(resultCountLabel) && 'ml-2')}
      >
        {resultCountLabel}
      </span>
    </div>
  );
}

export { FilterCategoryPanel } from './filter-category-panel';
export { FilterOptionsPanel } from './filter-options-panel';
